import {Component, computed, inject, input, model, signal} from '@angular/core';
import {CommonModule, formatNumber} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {InputNumberModule} from 'primeng/inputnumber';
import {InputTextModule} from 'primeng/inputtext';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {toSignal} from '@angular/core/rxjs-interop';
import {IconPosition} from './model-field-number.types';
import {DirectusTooltipComponent} from '../../../directus-tooltip/directus-tooltip.component';

@Component({
  selector: 'app-model-field-number',
  imports: [
    CommonModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    TranslocoPipe,
    DirectusTooltipComponent,
  ],
  templateUrl: './model-field-number.component.html',
  styleUrl: './model-field-number.component.scss',
})
export class ModelFieldNumberComponent {
  protected readonly IconPosition = IconPosition;

  private readonly translocoService = inject(TranslocoService);

  id = input.required<string>();
  label = input<string>('');
  tooltipKey = input<string>('');
  placeholder = input<string>('');
  isReadOnly = input<boolean>(false);
  smallLabel = input<boolean>(false);
  showButtons = input<boolean>(false);
  showClear = input<boolean>(false);
  mode = input<'decimal' | 'integer'>('decimal');
  step = input<number | undefined>(undefined);
  minFractionDigits = input<number>(0);
  maxFractionDigits = input<number>(2);
  alignRight = input<boolean>(false);
  icon = input<string | undefined>(undefined);
  iconPosition = input<IconPosition>(IconPosition.Begin);

  max = input<number | undefined>(undefined);
  min = input<number | undefined>(undefined);
  locale = input<string>('');

  isRequired = input<boolean>(false);

  value = model<number | null>(null);

  protected readonly isTouched = signal<boolean>(false);

  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly effectiveLocale = computed(() => this.locale() || this.activeLang());

  readonly isInvalid = computed(() => {
    if (!this.isRequired() || !this.isTouched()) return false;
    const v = this.value();
    return v === null || v === undefined;
  });

  readonly formattedReadOnlyValue = computed(() => {
    const val = this.value();
    if (val === null || val === undefined) return '';
    if (this.mode() === 'integer') return formatNumber(val, this.effectiveLocale(), '1.0-0');
    return formatNumber(
      val,
      this.effectiveLocale(),
      `1.${this.minFractionDigits()}-${this.maxFractionDigits()}`,
    );
  });

  readonly finalMin = computed(() => this.min() ?? 0);
  readonly finalMax = computed(() => this.max());

  protected markAsTouched(): void {
    this.isTouched.set(true);
  }
}
